import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { MessageCircle, Send, ChevronDown, ChevronUp, User, Shield } from "lucide-react";

import { API } from "../utils/api";

/**
 * AnswerComments - Inline comments/conversation for an exercise answer
 * Displays like a comment thread under the answer
 * @param {string} answerId - The answer ID
 * @param {Function} onNewMessage - Callback when a new message is sent
 */
const AnswerComments = ({ answerId, onNewMessage }) => {
  const { token, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (answerId) {
      fetchConversation();
    }
  }, [answerId]);

  const fetchConversation = async () => {
    if (!answerId) return;
    
    setLoading(true);
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await fetch(`${API}/answers/${answerId}/conversation`, {
        headers,
        credentials: "include",
      });

      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setUnreadCount(data.unread_count || 0);
        
        // Auto-expand if there are unread messages
        if (data.unread_count > 0) {
          setExpanded(true);
          // Mark as read
          await fetch(`${API}/answers/${answerId}/conversation/mark-read`, {
            method: "POST",
            headers,
            credentials: "include",
          });
          setUnreadCount(0);
        }
      }
    } catch (error) {
      console.error("Error fetching conversation:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !answerId) return;

    setSending(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const response = await fetch(`${API}/answers/${answerId}/conversation`, {
        method: "POST",
        headers,
        credentials: "include",
        body: JSON.stringify({ content: newMessage }),
      });

      if (response.ok) {
        const newMsg = await response.json();
        setMessages([...messages, newMsg]);
        setNewMessage("");
        setExpanded(true);
        onNewMessage?.();
        toast.success("Message envoyé");
      } else {
        toast.error("Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi");
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!answerId) {
    return null;
  }

  const hasMessages = messages.length > 0;

  return (
    <div className="mt-3 border-t border-slate-200 pt-3" data-testid="answer-comments">
      {/* Header - toggle to show/hide comments */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-sm text-slate-600 hover:text-[#101E4D] transition-colors w-full"
      >
        <MessageCircle className="w-4 h-4" />
        <span className="font-medium">
          {hasMessages ? `${messages.length} échange${messages.length > 1 ? 's' : ''}` : "Échanger avec mon coach"}
        </span>
        {unreadCount > 0 && (
          <span className="px-1.5 py-0.5 text-xs font-bold bg-[#FF6B35] text-white rounded-full">
            {unreadCount} nouveau{unreadCount > 1 ? 'x' : ''}
          </span>
        )}
        <span className="ml-auto">
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>

      {/* Expandable content */}
      {expanded && (
        <div className="mt-3 space-y-3">
          {/* Messages list */}
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <div className="w-5 h-5 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : hasMessages ? (
            <div className="space-y-2 max-h-[300px] overflow-y-auto">
              {messages.map((msg) => {
                const isMe = msg.sender_id === user?.user_id;
                const isAdmin = msg.sender_role === "admin";
                
                return (
                  <div
                    key={msg.message_id}
                    className={`flex gap-2 ${isMe ? "flex-row-reverse" : ""}`}
                  >
                    {/* Avatar */}
                    <div 
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                        isAdmin ? "bg-[#101E4D]" : "bg-slate-300"
                      }`}
                    >
                      {isAdmin ? (
                        <Shield className="w-3.5 h-3.5 text-white" />
                      ) : (
                        <User className="w-3.5 h-3.5 text-slate-600" />
                      )}
                    </div>
                    
                    {/* Message bubble */}
                    <div className={`flex-1 max-w-[85%] ${isMe ? "text-right" : ""}`}>
                      <div
                        className={`inline-block px-3 py-2 rounded-lg text-sm ${
                          isMe
                            ? "bg-[#FF6B35]/10 text-slate-700"
                            : isAdmin
                            ? "bg-[#101E4D]/10 text-slate-700"
                            : "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {!isMe && (
                          <p className="text-xs font-semibold text-[#101E4D] mb-1">
                            {msg.sender_name}
                          </p>
                        )}
                        <p className="whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      <p className="text-xs text-slate-400 mt-0.5">
                        {new Date(msg.created_at).toLocaleString("fr-FR", {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-2">
              Posez une question ou partagez vos réflexions avec votre coach
            </p>
          )}

          {/* Input field */}
          <div className="flex gap-2 items-end">
            <Textarea
              placeholder="Votre message pour le coach..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              className="min-h-[60px] max-h-[100px] resize-none text-sm flex-1"
              data-testid="comment-input"
            />
            <Button
              onClick={sendMessage}
              disabled={sending || !newMessage.trim()}
              size="sm"
              className="bg-[#FF6B35] hover:bg-[#E85D2A] h-[60px] px-3"
              data-testid="send-comment-btn"
            >
              {sending ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            Appuyez sur Entrée pour envoyer
          </p>
        </div>
      )}
    </div>
  );
};

export default AnswerComments;
